import { zodResolver } from '@hookform/resolvers/zod'
import { autoSignIn } from 'aws-amplify/auth'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { env } from 'src/config/env'
import AuthCard from 'src/features/auth/components/auth-card'
import AuthContent from 'src/features/auth/components/auth-content'
import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
import AuthTitle from 'src/features/auth/components/auth-title'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { useConfirmSignUp, useResendConfirmationCode, useSignUp } from 'src/shared/features/auth/api'
import { useAddToCognitoGroup } from 'src/shared/features/auth/api/use-add-to-cognito-group'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import { useToast } from 'src/shared/hooks/use-toast'
import type { SignupFormData } from 'src/shared/schemas/forms/auth'
import { signupSchema } from 'src/shared/schemas/forms/auth'
import { useEventStore } from 'src/shared/store/event-store'

import AuthCodeInput from '../auth-code-input'

interface ProfessionalSignupFormProps {
  onSuccess: () => void
  onStepChange?: (step: 'signup' | 'verify') => void
}

interface SignupFormProps {
  onSuccess?: (email: string) => void
}

const SignupForm = ({ onSuccess }: SignupFormProps) => {
  // const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const signUp = useSignUp()
  const { toast } = useToast()
  const { mutateAsync: addToCognitoGroup } = useAddToCognitoGroup()

  const onSignup = async (data: SignupFormData) => {
    setIsLoading(true)
    try {
      await signUp.mutateAsync({
        email: data.email,
        password: data.password,
      })

      try {
        await addToCognitoGroup()
      } catch (error) {
        console.error(error)
      }
      // navigate(paths.auth.confirmAccount.getHref(data.email, redirectTo || ''))
      onSuccess?.(data.email)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        if (error.name === 'UsernameExistsException') {
          toast({
            title: 'Account Already Exists',
            description: 'An account with this email already exists. Please sign in instead.',
            variant: 'destructive',
          })
          // navigate(paths.auth.login.getHref(redirectTo || ''))
          // TODO: Add redirect to login page
          return setIsLoading(false)
        }
        toast({
          title: 'Signup failed',
          description: 'Sorry, there was an error signing up. Please try again.',
          variant: 'destructive',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
      <AuthContent>
        <AuthTitle title="Join the network" />
        <AuthSubtitle description="Where homeownership happens." />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSignup)} className="space-y-5">
            <div className="flex flex-col gap-2">
              <AuthInputFormField control={form.control} name="email" label="Email" type="email" />
              <AuthInputFormField control={form.control} name="password" label="Password" type="password" />
              <AuthInputFormField
                control={form.control}
                name="confirmPassword"
                label="Confirm Password"
                type="password"
              />
            </div>
            {/* <p className="mt-4 text-center text-xs text-muted md:text-sm">
              By clicking <span className="text-primary">&apos;Sign up&apos;</span>, you confirm that you have read and
              agree to our{' '}
              <a
                href={env.LINK_TO_TERMS_OF_USE}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={env.LINK_TO_PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Privacy Policy
              </a>
            </p> */}
            <AuthSubmitButton type="submit" className="mt-4" isLoading={signUp.isPending || isLoading}>
              {signUp.isPending || isLoading ? 'Signing you up...' : 'Sign up'}
            </AuthSubmitButton>
          </form>
        </Form>
      </AuthContent>
    </AuthCard>
  )
}

const confirmSignupSchema = z.object({
  code: z.string().min(6, 'Confirmation code must be 6 characters'),
})

type ConfirmSignupFormData = z.infer<typeof confirmSignupSchema>

const sendEmailConfirmedEvent = async (email: string, anonymousId: string, error?: string) => {
  sendEvent({
    anonymousId: anonymousId || 'no anonymousId',
    eventType: 'flow',
    info: {
      email,
      flow_type: 'onboarding',
      flow_step: 'email_confirmed',
      error,
    },
  })
}

interface ConfirmSignupFormProps {
  onSuccess?: () => void
  email: string
}

const ConfirmSignupForm = ({ onSuccess, email }: ConfirmSignupFormProps) => {
  const confirmSignUp = useConfirmSignUp()
  const resendCode = useResendConfirmationCode()
  const { toast } = useToast()

  const { mutateAsync: addToCognitoGroup } = useAddToCognitoGroup()

  const confirmForm = useForm<ConfirmSignupFormData>({
    resolver: zodResolver(confirmSignupSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
    },
  })

  const { anonymousId, setAnonymousId } = useEventStore()

  if (!anonymousId) {
    setAnonymousId()
  }

  const onConfirm = async (data: ConfirmSignupFormData) => {
    try {
      const result = await confirmSignUp.mutateAsync({ email, code: data.code })

      sendEmailConfirmedEvent(email, anonymousId || 'no anonymousId')

      if (result.isSignUpComplete && result.nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        try {
          const { nextStep } = await autoSignIn()

          if (nextStep.signInStep === 'DONE') {
            try {
              await addToCognitoGroup()
            } catch (error) {
              console.error(error)
            }

            // navigate(paths.auth.onboarding.getHref(redirectTo || ''))
            onSuccess?.()
          } else {
            throw new Error('Auto sign in failed')
          }
          // navigate to onboarding if auto sign in is successful and if not it will automatically navigate to login
          return
        } catch (error) {
          console.error(error)
          try {
            await addToCognitoGroup()
          } catch (error) {
            console.error(error)
          }

          // navigate(paths.auth.onboarding.getHref(redirectTo || ''))
          onSuccess?.()
          // navigate(paths.auth.login.getHref(redirectTo || ''))
          return
        }
      } else if (result.isSignUpComplete && result.nextStep?.signUpStep === 'DONE') {
        try {
          await addToCognitoGroup()
        } catch (error) {
          console.error(error)
        }

        // navigate(paths.auth.onboarding.getHref(redirectTo || ''))
        onSuccess?.()
        return
      }

      try {
        await addToCognitoGroup()
      } catch (error) {
        console.error(error)
      }

      toast({
        title: 'Account confirmed successfully',
        description: 'Log in to your account to continue.',
      })
      onSuccess?.()
      // navigate(paths.auth.login.getHref(redirectTo || ''))
    } catch (error) {
      sendEmailConfirmedEvent(
        email,
        anonymousId || 'no anonymousId',
        error instanceof Error ? error.message : 'An unknown error occurred',
      )
      toast({
        variant: 'destructive',
        title: 'Confirmation failed',
        description: error instanceof Error ? error.message : 'An error occurred - please request a new code',
      })
    }
  }

  const handleResendCode = async () => {
    try {
      await resendCode.mutateAsync(email)
      toast({
        title: 'Code resent',
        description: 'A new confirmation code has been sent to your email.',
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to resend code',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      })
    }
  }

  return (
    <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
      <AuthContent>
        <AuthTitle title="Enter 6 digit code" />
        <AuthSubtitle
          description={`You've received a code at ${email}. Please check your inbox and spam folders to retrieve it.`}
        />
        <Form {...confirmForm}>
          <form onSubmit={confirmForm.handleSubmit(onConfirm)} className="space-y-4">
            <AuthCodeInput
              isConsumerLogin={false}
              control={confirmForm.control}
              name="code"
              className="py-2"
              inputClassName="h-[55px] border-none bg-[#F8F7F5] rounded-[12px] w-full max-w-none"
            />
            <p className="mt-4 text-center text-xs text-muted md:text-sm">
              By clicking <span className="text-primary">&apos;Confirm&apos;</span>, you confirm that you have read and
              agree to our{' '}
              <a
                href={env.LINK_TO_TERMS_OF_USE}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={env.LINK_TO_PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Privacy Policy
              </a>
            </p>
            <Button
              type="submit"
              className="h-[55px] w-full rounded-full bg-primary"
              disabled={confirmSignUp.isPending}
            >
              {confirmSignUp.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : 'Confirm'}
            </Button>
          </form>
        </Form>
        <div className="text-center">
          <Button
            variant="link"
            onClick={handleResendCode}
            type="button"
            disabled={resendCode.isPending}
            className="text-sm text-muted underline hover:text-primary"
          >
            {resendCode.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : 'Resend Code'}
          </Button>
        </div>
      </AuthContent>
    </AuthCard>
  )
}

const ProfessionalSignupForm = ({ onSuccess, onStepChange }: ProfessionalSignupFormProps) => {
  const [step, setStep] = useState<'signup' | 'verify'>('signup')
  const [email, setEmail] = useState('')

  const handleSignupSuccess = (email: string) => {
    setEmail(email)
    setStep('verify')
    onStepChange?.('verify')
  }

  const handleConfirmSuccess = () => {
    onSuccess?.()
  }

  if (step === 'signup') {
    return <SignupForm onSuccess={handleSignupSuccess} />
  }

  return <ConfirmSignupForm onSuccess={handleConfirmSuccess} email={email} />
}

export default ProfessionalSignupForm
